import { useEffect, useState } from "react";
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import apiClient from "@/services";
import { Button } from "./ui/button";
import ImageViewer from "./ImageViewer";
import { TrashIcon } from "@radix-ui/react-icons";

const getFirebaseUrl = async (path) => {
  try {
    const storage = getStorage();
    const storageRef = ref(storage, path);
    return await getDownloadURL(storageRef);
  } catch (error) {
    console.error("Error getting download URL:", error);
  }
};

const extractFilePath = (fileUrl) => {
  const match = fileUrl.match(/\/o\/(.*?)\?/);
  return match ? decodeURIComponent(match[1]) : null;
};

function Gallery({ user, allImages, requestId, referenceId }) {
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState(allImages);
  const [updatedImages, setUpdatedImages] = useState();

  const deleteFile = async (fileUrl) => {
    try {
      setLoading(true);
      const collection = referenceId.startsWith("SR")
        ? "seller_requests"
        : referenceId.startsWith("BR")
        ? "buyer_requests"
        : undefined;

      const response = await apiClient.post("/deleteFileFromStorage", {
        fileUrl,
        requestId,
        collection,
      });

      if (response.status === 201) {
        // Extract the file path from the download URL
        const filePath = extractFilePath(fileUrl);
        if (!filePath) {
          throw new Error("Invalid file URL. Could not extract the file path.");
        }

        // Filter the images array to exclude the object with the matching URL
        const newImages = images.filter((image) => image.url !== filePath);

        // Only update the state if the images array has changed
        if (newImages.length !== images.length) {
          setImages(newImages);
        } else {
          // If no images were deleted (e.g., single image case)
          setImages([]);
        }
      }

      alert("Image deleted successfully");
    } catch (error) {
      alert("Failed to deleted image from storage");
      console.error("Error:", error.message || error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchUrls = async () => {
      if (images && images.length > 0) {
        const updatedImagesArray = await Promise.all(
          images.map(async (image) => {
            const imageUrl = await getFirebaseUrl(image.url);
            return { ...image, url: imageUrl }; // Add the updated URL to the image object
          })
        );
        setUpdatedImages(updatedImagesArray); // Update the state with the new URLs
      } else {
        setUpdatedImages([]); // Ensure state is cleared if no images exist
      }
    };

    fetchUrls();
  }, [images, setImages]); // Run the effect when the images prop changes

  if (loading) {
    return (
      <div className="flex justify-center items-center">
        <div className="my-8 animate-spin rounded-full h-10 w-10 border-t-2 border-green"></div>
      </div>
    );
  }

  return (
    <div className="productWrapper">
      {updatedImages &&
        updatedImages.length > 0 &&
        updatedImages.map((image, idx) => (
          <div key={idx}>
            <div className="productCard cursor-pointer">
              <ImageViewer src={image.url} alt={image?.description} />
              <p className="p-2 text-sm text-muted break-words overflow-hidden">
                {image?.description}
              </p>
            </div>
            {["admin", "buyer_agent", "seller_agent"].includes(user.role) && (
              <Button
                className="w-[180px] my-3"
                onClick={() => deleteFile(image.url)}
              >
                <TrashIcon /> Remove
              </Button>
            )}
          </div>
        ))}
    </div>
  );
}

export default Gallery;
